import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useMutation } from 'react-query';
import api from '../utils/api';
import { CreditCard, Lock, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#f1f5f9',
      fontFamily: 'Cairo, sans-serif',
      fontSize: '16px',
      '::placeholder': { color: '#64748b' },
      backgroundColor: 'transparent',
    },
    invalid: { color: '#ef4444' },
  },
};

function CheckoutForm({ bookingData, clientSecret }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const confirmMutation = useMutation((data) =>
    api.post('/payments/confirm', data)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    const card = elements.getElement(CardElement);
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card },
    });

    if (result.error) {
      setError(result.error.message);
      setProcessing(false);
    } else if (result.paymentIntent.status === 'succeeded') {
      try {
        await confirmMutation.mutateAsync({
          paymentIntentId: result.paymentIntent.id,
          bookingUuid: bookingData.uuid,
        });
        setSucceeded(true);
        setTimeout(() => navigate(`/dashboard/bookings/${bookingData.uuid}`), 2000);
      } catch {
        setError('حدث خطأ أثناء تأكيد الحجز. يرجى التواصل مع الدعم.');
        setProcessing(false);
      }
    }
  };

  if (succeeded) {
    return (
      <div className="text-center py-16">
        <CheckCircle className="w-20 h-20 text-primary-400 mx-auto mb-6" />
        <h2 className="text-3xl font-bold text-white mb-3">تم الدفع بنجاح!</h2>
        <p className="text-slate-400 mb-6">جارٍ تحويلك إلى تفاصيل الحجز...</p>
        <div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-dark-700 rounded-xl p-6 border border-dark-600">
        <label className="block text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-primary-400" />
          بيانات البطاقة البنكية
        </label>
        <div className="bg-dark-600 rounded-lg p-4 border border-dark-500">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
        <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
          <Lock className="w-3 h-3" />
          مدفوعاتك مشفرة بالكامل عبر Stripe
        </p>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-700/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full btn-primary py-4 text-lg font-bold flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {processing ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            جارٍ المعالجة...
          </>
        ) : (
          <>
            <Lock className="w-5 h-5" />
            ادفع {bookingData?.totalAmount?.toLocaleString()} ر.س
          </>
        )}
      </button>

      <div className="flex items-center justify-center gap-4 text-slate-500 text-xs">
        <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> SSL مشفر</span>
        <span>•</span>
        <span>مدعوم بـ Stripe</span>
        <span>•</span>
        <span>PCI DSS متوافق</span>
      </div>
    </form>
  );
}

export default function PaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState('');
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const state = location.state;
    if (!state?.bookingData) {
      navigate('/');
      return;
    }
    setBookingData(state.bookingData);

    api.post('/payments/create-intent', {
      bookingUuid: state.bookingData.uuid,
      amount: state.bookingData.totalAmount,
    })
      .then((res) => {
        setClientSecret(res.data.clientSecret);
        setLoading(false);
      })
      .catch(() => {
        setError('فشل إنشاء جلسة الدفع. يرجى المحاولة مجدداً.');
        setLoading(false);
      });
  }, [location, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">جارٍ تحضير صفحة الدفع...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center p-4">
        <div className="text-center bg-dark-800 rounded-2xl p-10 max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">خطأ في الدفع</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <button onClick={() => navigate(-1)} className="btn-primary px-6 py-3">
            العودة للخلف
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-primary-500/20 border border-primary-500/30 text-primary-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Lock className="w-4 h-4" />
            صفحة الدفع الآمنة
          </div>
          <h1 className="text-3xl font-bold text-white">إتمام الحجز</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700 h-fit">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary-400" />
              ملخص الحجز
            </h2>

            {bookingData && (
              <div className="space-y-4">
                <div className="bg-dark-700 rounded-xl p-4">
                  <h3 className="font-semibold text-white text-lg">{bookingData.listingTitle}</h3>
                  <p className="text-slate-400 text-sm mt-1">{bookingData.listingLocation}</p>
                </div>

                <div className="space-y-3">
                  {[
                    { label: 'التاريخ', value: bookingData.date },
                    { label: 'وقت البدء', value: bookingData.startTime },
                    { label: 'المدة', value: `${bookingData.duration} ساعة` },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between text-sm">
                      <span className="text-slate-400">{label}</span>
                      <span className="text-white font-medium">{value}</span>
                    </div>
                  ))}
                </div>

                <hr className="border-dark-600" />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">المبلغ الأساسي</span>
                    <span className="text-white">{bookingData.subtotal?.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">رسوم المنصة (10%)</span>
                    <span className="text-white">{bookingData.commission?.toLocaleString()} ر.س</span>
                  </div>
                  <hr className="border-dark-600" />
                  <div className="flex justify-between font-bold text-lg">
                    <span className="text-white">الإجمالي</span>
                    <span className="text-primary-400">{bookingData.totalAmount?.toLocaleString()} ر.س</span>
                  </div>
                </div>

                <div className="bg-primary-500/10 border border-primary-500/20 rounded-xl p-4 mt-4">
                  <p className="text-primary-300 text-xs text-center">
                    ✓ سيتم إرسال تأكيد الحجز ورمز QR على بريدك الإلكتروني فور إتمام الدفع
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Payment Form */}
          <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary-400" />
              بيانات الدفع
            </h2>

            {clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CheckoutForm bookingData={bookingData} clientSecret={clientSecret} />
              </Elements>
            )}
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate(-1)}
            className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 mx-auto"
          >
            <ArrowRight className="w-4 h-4" />
            العودة إلى الحجز
          </button>
        </div>
      </div>
    </div>
  );
}
